import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DownloadIcon from "@mui/icons-material/Download";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import React from "react";
import { AnalyticExport } from "../../shared/types/analyticExport.type";
import { inUserTz } from "./CreateExport"; // adjust path
import { useTranslation } from "react-i18next";
import { UseFileDownload } from "../../hooks/useFileDownload";

type Props = {
  exports?: AnalyticExport[];
  onRetry?: (item: AnalyticExport) => void;
  retryingId?: number;
  error?: string | null;
  isFetching?: boolean;
};

const fmtShort = (iso: string) => inUserTz(iso).format("DD MMM");
const fmtLong = (iso: string) => inUserTz(iso).format("DD MMM YYYY");
const fmtTime = (iso: string) => inUserTz(iso).format("HH:mm");
const isToday = (iso: string) => inUserTz(iso).isSame(inUserTz(), "day");

const formatRange = (start?: string | null, end?: string | null, t?: any) => {
  if (!start && !end) return t("export:allData");
  if (start && end) {
    if (inUserTz(start).isSame(inUserTz(end), "day")) return fmtLong(end);
    const sameYear = inUserTz(start).isSame(inUserTz(end), "year");
    return `${sameYear ? fmtShort(start) : fmtLong(start)} → ${fmtLong(end)}`;
  }
  if (start)
    return `${t("training:qualificationItem:from")} ${fmtLong(start as string)}`;
  return `${t("daterangepicker:toLabel")} ${fmtLong(end as string)}`;
};

const STATUS: Record<
  number,
  { label: string; color: "warning" | "success" | "error"; spinner?: boolean }
> = {
  0: { label: "export:status:pending", color: "warning" },
  1: { label: "export:status:generating", color: "warning", spinner: true },
  2: { label: "export:status:ready", color: "success" },
  4: { label: "export:status:failed", color: "error" },
};

const StatusChip = ({ status }: { status: AnalyticExport["status"] }) => {
  const s = STATUS[status as number];
  const { t } = useTranslation();
  if (!s) return null;
  return (
    <Chip
      size="small"
      label={t(s.label)}
      icon={
        s.spinner ? (
          <CircularProgress size={11} thickness={6} color="inherit" />
        ) : (
          <></>
        )
      }
      sx={(t) => ({
        fontWeight: 600,
        color: t.palette[s.color].main,
        bgcolor: alpha(t.palette[s.color].main, 0.1),
        "& .MuiChip-icon": { color: "inherit" },
      })}
    />
  );
};

const Action = ({
  item,
  onRetry,
  retrying,
}: {
  item: AnalyticExport;
  onRetry?: (item: AnalyticExport) => void;
  retrying: boolean;
}) => {
  const { t } = useTranslation();
  const { download, isDownloading } = UseFileDownload();
  if (item.status === 2 && item.file_url) {
    return (
      <LoadingButton
        size="small"
        variant="outlined"
        color="inherit"
        loading={isDownloading}
        onClick={() => download(item.file_url)}
        startIcon={<DownloadIcon />}
        sx={{ borderColor: "divider", whiteSpace: "nowrap" }}
      >
        {t("button:download")}
      </LoadingButton>
    );
  }
  if (item.status === 4) {
    return (
      <LoadingButton
        size="small"
        variant="outlined"
        color="inherit"
        loading={retrying}
        disabled={!onRetry}
        loadingPosition="start"
        startIcon={<ReplayIcon />}
        onClick={() => onRetry?.(item)}
        sx={{ borderColor: "divider", whiteSpace: "nowrap" }}
      >
        {t("button:retry")}
      </LoadingButton>
    );
  }
  return (
    <Typography variant="caption" color="text.disabled">
      —
    </Typography>
  );
};

const headCell = {
  fontWeight: 600,
  fontSize: 12,
  color: "text.secondary",
  py: 1.25,
  whiteSpace: "nowrap",
  bgcolor: (t: Theme) =>
    t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
} as const;

function ExportHistory({
  exports = [],
  onRetry,
  retryingId,
  error,
  isFetching,
}: Props) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.75}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t("eap:export:recentExports")}
        </Typography>
        {exports.length > 0 && (
          <Chip
            size="small"
            label={exports.length}
            sx={{ height: 20, fontWeight: 600 }}
          />
        )}
      </Stack>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, maxHeight: 400, overflowY: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={(t) => ({ bgcolor: t.palette.action.hover })}>
              <TableCell sx={headCell}>{t("export:requestedAt")}</TableCell>
              <TableCell sx={headCell}>{t("period")}</TableCell>
              <TableCell sx={headCell}>{t("equimetre:table:status")}</TableCell>
              <TableCell align="center" sx={headCell}>
                {t("settings:actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 6, border: 0 }}>
                  <Stack alignItems="center" spacing={1}>
                    <InboxOutlinedIcon
                      sx={{ fontSize: 32, color: "text.disabled" }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t("eap:export:noExports")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("eap:export:noExportsDesc")}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              exports.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {isToday(item.created_at)
                        ? "Aujourd'hui"
                        : fmtLong(item.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtTime(item.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarMonthIcon
                        sx={{ fontSize: 16, color: "text.disabled" }}
                      />
                      <Typography variant="body2">
                        {formatRange(
                          item.data_range_start,
                          item.data_range_end,
                          t,
                        )}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                  </TableCell>
                  <TableCell align="center">
                    <Action
                      item={item}
                      onRetry={onRetry}
                      retrying={retryingId === item.id}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export default ExportHistory;
